import { useEffect, useState } from "react";
import http from "@/api";
import { useAuth } from "@/auth";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 10;

const STATUS = {
  complete: { label: "Completo", bar: "#16A34A", chip: { background: "#DCFCE7", color: "#166534" } },
  partial: { label: "En curso", bar: "#D97706", chip: { background: "#FEF3C7", color: "#92400E" } },
  none: { label: "Sin iniciar", bar: "#DC2626", chip: { background: "#FEE2E2", color: "#991B1B" } },
  not_applicable: { label: "No aplica este mes", bar: "#A5A399", chip: { background: "#F1F0EC", color: "#6B6A62" } },
};

function ProgressBar({ pct, status }) {
  const meta = STATUS[status] || STATUS.none;
  return (
    <div className="h-2.5 rounded-full bg-[#ECEAE3] overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(pct ?? 0, 100)}%`, background: meta.bar }} />
    </div>
  );
}

function StatusChip({ status }) {
  const meta = STATUS[status] || STATUS.none;
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={meta.chip}>{meta.label}</span>;
}

function ProgressRow({ label, row, testid }) {
  const na = row.status === "not_applicable";
  return (
    <div className="py-2" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="text-sm font-semibold">{label}</div>
        <StatusChip status={row.status} />
      </div>
      {na ? (
        <div className="text-[11px] text-[var(--ink-soft)]">No hay indicadores por capturar en este periodo.</div>
      ) : (
        <>
          <ProgressBar pct={row.pct} status={row.status} />
          <div className="flex justify-between text-[11px] text-[var(--ink-soft)] mt-1">
            <span><b className="text-[var(--ink)]">{Math.round(row.pct ?? 0)}%</b> · {row.captured} de {row.expected} capturadas</span>
            <span>{row.pending > 0 ? `Faltan ${row.pending}` : "Sin pendientes"}</span>
          </div>
        </>
      )}
    </div>
  );
}

export default function CaptureProgressOverview() {
  const { user } = useAuth();
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pillarFilter, setPillarFilter] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    setLoading(true);
    http.get("/admin/capture-progress", { params: { period } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [user, period]);

  if (!user || user.role !== "admin") return null;

  const pillars = data?.pillars || [];
  const evaluators = data?.evaluators || [];
  const othersWithData = (data?.others || []).filter((o) => o.expected > 0 && (!pillarFilter || o.pillar === pillarFilter));

  // Con filtro de pilar solo se listan los evaluadores que lo tienen asignado, mostrando solo ese pilar.
  const filteredEvaluators = evaluators
    .map((ev) => ({ ...ev, pillars: pillarFilter ? ev.pillars.filter((p) => p.pillar === pillarFilter) : ev.pillars }))
    .filter((ev) => ev.pillars.length > 0);
  const totalPages = Math.max(1, Math.ceil(filteredEvaluators.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageEvaluators = filteredEvaluators.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className="sketch-border bg-[var(--cream-soft)] p-6 mb-10" data-testid="capture-progress">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">seguimiento del mes</div>
          <h3 className="font-display text-2xl">Avance de captura</h3>
          <p className="text-xs text-[var(--ink-soft)] mt-1">
            Cuenta lo que ya está capturado sobre lo que corresponde evaluar este mes, sin importar quién lo capturó.
            Los indicadores trimestrales, semestrales o anuales solo cuentan en su mes.
          </p>
        </div>
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">Periodo</div>
          <input type="month" value={period} onChange={(e) => { setPeriod(e.target.value); setPage(1); }}
            className="input-field" data-testid="progress-period-input" />
        </label>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[var(--ink-soft)] py-6" data-testid="progress-loading">
          <Loader2 size={14} className="animate-spin" /> Calculando avance…
        </div>
      )}

      {!loading && data && (
        <div className="grid lg:grid-cols-2 gap-6">
          <section className="bg-white rounded-lg border border-[var(--line)] p-4">
            <h4 className="font-display text-lg mb-1">Avance por pilar</h4>
            <p className="text-[11px] text-[var(--ink-soft)] mb-2">¿Cuánto del pilar ya está evaluado?</p>
            <div className="divide-y divide-[var(--line)]">
              {pillars.map((p) => (
                <ProgressRow key={p.pillar} label={p.label} row={p} testid={`progress-pillar-${p.pillar}`} />
              ))}
            </div>
            <div className="text-[11px] text-[var(--ink-soft)] mt-3">{data.suppliers_count} proveedores en el programa</div>
          </section>

          <section className="bg-white rounded-lg border border-[var(--line)] p-4">
            <h4 className="font-display text-lg mb-1">Avance por evaluador</h4>
            <div className="flex flex-wrap items-end justify-between gap-2 mb-2">
              <p className="text-[11px] text-[var(--ink-soft)]">Cada quien sobre los proveedores y pilares que le tocan.</p>
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-0.5">Pilar</div>
                <select value={pillarFilter} onChange={(e) => { setPillarFilter(e.target.value); setPage(1); }}
                  className="input-field text-xs py-1" data-testid="progress-pillar-filter">
                  <option value="">Todos los pilares</option>
                  {pillars.map((p) => <option key={p.pillar} value={p.pillar}>{p.label}</option>)}
                </select>
              </label>
            </div>
            {evaluators.length > 0 && filteredEvaluators.length === 0 && (
              <div className="text-sm text-[var(--ink-soft)] italic" data-testid="progress-no-match">
                Ningún evaluador tiene asignado este pilar.
              </div>
            )}
            {evaluators.length === 0 && (
              <div className="text-sm text-[var(--ink-soft)] italic" data-testid="progress-no-evaluators">
                Aún no hay evaluadores con pilares asignados. Asígnalos en Configuración → Usuarios y permisos.
              </div>
            )}
            <div className="space-y-4">
              {pageEvaluators.map((ev) => (
                <div key={ev.id} data-testid={`progress-evaluator-${ev.id}`}>
                  <div className="font-semibold text-sm border-b border-[var(--line)] pb-1">{ev.name}</div>
                  <div className="divide-y divide-[var(--line)]">
                    {ev.pillars.map((p) => (
                      <ProgressRow key={p.pillar} label={p.label} row={p} testid={`progress-evaluator-${ev.id}-${p.pillar}`} />
                    ))}
                  </div>
                </div>
              ))}
              {filteredEvaluators.length > PAGE_SIZE && (
                <div className="flex items-center justify-between text-[11px] text-[var(--ink-soft)] pt-1" data-testid="progress-pagination">
                  <span>
                    {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredEvaluators.length)} de {filteredEvaluators.length} evaluadores
                  </span>
                  <div className="flex items-center gap-1">
                    <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}
                      className="p-1 rounded border border-[var(--line)] disabled:opacity-40" aria-label="Página anterior" data-testid="progress-page-prev">
                      <ChevronLeft size={14} />
                    </button>
                    <span className="px-2">Página {currentPage} de {totalPages}</span>
                    <button type="button" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}
                      className="p-1 rounded border border-[var(--line)] disabled:opacity-40" aria-label="Página siguiente" data-testid="progress-page-next">
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
              {othersWithData.length > 0 && (
                <div data-testid="progress-others">
                  <div className="font-semibold text-sm border-b border-[var(--line)] pb-1">Otros / sin evaluador asignado</div>
                  <p className="text-[11px] text-[var(--ink-soft)] my-1">
                    Proveedores de estos pilares que ningún evaluador tiene asignado; lo capturado lo hizo un administrador.
                  </p>
                  {othersWithData.map((o) => {
                    const pct = o.expected ? (o.captured / o.expected) * 100 : null;
                    const status = o.captured >= o.expected ? "complete" : o.captured === 0 ? "none" : "partial";
                    const label = pillars.find((p) => p.pillar === o.pillar)?.label || o.pillar;
                    return <ProgressRow key={o.pillar} label={label} testid={`progress-others-${o.pillar}`}
                      row={{ ...o, pct, status }} />;
                  })}
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
